import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  CheckCheck,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Lightbulb,
  Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import {
  useNotificationsQuery,
  useNotificationQuery,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
  useClearAllNotifications
} from '../hooks/useQueries';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmModal from '../components/common/ConfirmModal';

// Notification type config
const TYPE_CONFIG = {
  info: { icon: Info, color: '#3b82f6', bgColor: '#eff6ff' },
  success: { icon: CheckCircle, color: '#22c55e', bgColor: '#f0fdf4' },
  warning: { icon: AlertTriangle, color: '#f59e0b', bgColor: '#fffbeb' },
  error: { icon: XCircle, color: '#ef4444', bgColor: '#fef2f2' },
  update: { icon: Zap, color: '#8b5cf6', bgColor: '#f5f3ff' },
  tip: { icon: Lightbulb, color: '#06b6d4', bgColor: '#ecfeff' }
};

// Format relative time
function formatRelativeTime(date) {
  const now = new Date();
  const notifDate = new Date(date);
  const diffMs = now - notifDate;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return notifDate.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  });
}

// Notification List View
function NotificationList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasEncryptionKey } = useAuth();

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [clearAllConfirm, setClearAllConfirm] = useState(false);

  const { data, isLoading } = useNotificationsQuery({}, {
    enabled: hasEncryptionKey
  });

  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteMutation = useDeleteNotification();
  const clearAllMutation = useClearAllNotifications();

  const notifications = data?.notifications || [];
  const hasUnread = notifications.some(n => !n.isRead);

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      try {
        await markAsReadMutation.mutateAsync(notification._id);
      } catch {
        // Ignore errors, still navigate
      }
    }

    // Navigate to detail view
    navigate(`/notifications/${notification._id}`);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeleteConfirm({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.id) {
      try {
        await deleteMutation.mutateAsync(deleteConfirm.id);
        toast.success('Notification cleared');
      } catch {
        toast.error('Failed to clear notification');
      }
    }
    setDeleteConfirm({ open: false, id: null });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 transition-colors xl:hidden"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-bold text-surface-900">Notifications</h1>
        </div>

        <div className="flex items-center gap-1.5">
          {hasUnread && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)' }}
              title="Mark all read"
            >
              <CheckCheck className="w-4 h-4" />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={() => setClearAllConfirm(true)}
              disabled={clearAllMutation.isPending}
              className="p-2 rounded-lg transition-colors text-expense-600 hover:bg-expense-50"
              title="Clear all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Notification List */}
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center" style={{ minHeight: 'calc(60vh)' }}>
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-surface-100 flex items-center justify-center">
            <Bell className="w-7 h-7 text-surface-300" />
          </div>
          <p className="text-surface-900 font-semibold text-sm">No notifications yet</p>
          <p className="text-xs text-surface-400 mt-1">
            We'll notify you about important updates
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => {
            const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.info;
            const Icon = config.icon;

            return (
              <div
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white rounded-xl border p-3 cursor-pointer transition-all active:scale-[0.99] ${
                  notification.isRead
                    ? 'border-surface-100'
                    : 'border-l-[3px] shadow-sm'
                }`}
                style={!notification.isRead ? { borderLeftColor: config.color } : {}}
              >
                <div className="flex gap-2.5">
                  {/* Icon */}
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: config.bgColor }}
                  >
                    <Icon className="w-4 h-4" style={{ color: config.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-sm text-surface-900 line-clamp-1 ${!notification.isRead ? 'font-semibold' : 'font-medium'}`}>
                        {notification.title}
                      </h3>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!notification.isRead && (
                          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config.color }} />
                        )}
                        <span className="text-[10px] text-surface-400 whitespace-nowrap">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                        <button
                          onClick={(e) => handleDeleteClick(e, notification._id)}
                          className="p-1 rounded-md text-surface-300 hover:text-expense-500 hover:bg-expense-50 transition-colors"
                          title="Clear"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-surface-500 line-clamp-2 mt-0.5">
                      {notification.message.replace(/<[^>]*>/g, '').substring(0, 100)}
                      {notification.message.length > 100 ? '...' : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Clear Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
        title="Clear Notification"
        message="Are you sure you want to clear this notification?"
        confirmText="Clear"
        variant="danger"
      />

      {/* Clear All Confirmation */}
      <ConfirmModal
        isOpen={clearAllConfirm}
        onClose={() => setClearAllConfirm(false)}
        onConfirm={async () => {
          try {
            await clearAllMutation.mutateAsync();
            toast.success('All notifications cleared');
          } catch {
            toast.error('Failed to clear notifications');
          }
          setClearAllConfirm(false);
        }}
        title="Clear All Notifications"
        message="Are you sure you want to clear all your notifications? This action cannot be undone."
        confirmText="Clear All"
        variant="danger"
      />
    </div>
  );
}

// Auto-linkify URLs in text - converts plain URLs to clickable links
function linkifyText(text) {
  if (!text) return text;
  // Match URLs that aren't already inside href="..."
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;word-break:break-all">${url}</a>`;
  });
}

// Notification Detail View
function NotificationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatDate } = useConfig();
  const { hasEncryptionKey } = useAuth();

  const { data: notification, isLoading } = useNotificationQuery(id, {
    enabled: hasEncryptionKey && !!id
  });

  const markAsReadMutation = useMarkNotificationAsRead();

  // Mark as read when viewing
  useState(() => {
    if (notification && !notification.isRead) {
      markAsReadMutation.mutate(id);
    }
  }, [notification, id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">Notification not found</p>
        <button
          onClick={() => navigate('/notifications')}
          className="mt-4 text-primary-600 font-medium"
        >
          Back to Notifications
        </button>
      </div>
    );
  }

  const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.info;
  const Icon = config.icon;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-bold text-surface-900">Notification</h1>
      </div>

      {/* Notification Card */}
      <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {/* Header */}
        <div
          className="p-4 sm:p-6"
          style={{ backgroundColor: config.bgColor }}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: config.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-surface-900 break-words">
                {notification.title}
              </h2>
              <p className="text-xs sm:text-sm text-surface-500 mt-1">
                {new Date(notification.createdAt).toLocaleDateString('en-IN', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div
            className="prose prose-sm max-w-none text-surface-700 break-words [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all [&_a]:hover:text-blue-800"
            dangerouslySetInnerHTML={{ __html: linkifyText(notification.message) }}
          />

          {/* Action URL */}
          {notification.actionUrl && (
            <button
              onClick={() => navigate(notification.actionUrl)}
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-xl font-medium transition-colors hover:opacity-90"
              style={{ backgroundColor: config.color, color: '#ffffff' }}
            >
              <span>View Transactions</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Component - decides which view to show
export default function NotificationsPage() {
  const { id } = useParams();

  if (id) {
    return <NotificationDetail />;
  }

  return <NotificationList />;
}
